import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, ClockIcon, CheckCircleIcon, MapPinIcon } from 'lucide-react';
interface VaccineAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  vaccineName: string;
}
export function VaccineAppointmentModal({
  isOpen,
  onClose,
  vaccineName
}: VaccineAppointmentModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 4000);
  };
  return <AnimatePresence data-id="element-306">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]" data-id="element-307" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 m-auto w-full max-w-xl h-fit bg-white rounded-3xl shadow-2xl z-[101] overflow-hidden" data-id="element-308">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-309">
              <div className="flex items-center gap-3" data-id="element-310">
                <div className="w-10 h-10 bg-clinical-blue/10 rounded-xl flex items-center justify-center text-clinical-blue" data-id="element-311">
                  <CalendarIcon className="w-5 h-5" data-id="element-312" />
                </div>
                <div data-id="element-313">
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight" data-id="element-314">
                    Schedule {vaccineName}
                  </h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-315">
                    Secure Appointment Booking
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors" data-id="element-316">
                <XIcon className="w-5 h-5 text-slate-400" data-id="element-317" />
              </button>
            </div>

            <div className="p-8" data-id="element-318">
              {submitted ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center py-12" data-id="element-319">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-320">
                    <CheckCircleIcon className="w-10 h-10" data-id="element-321" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2 uppercase tracking-tight" data-id="element-322">
                    Appointment Confirmed!
                  </h3>
                  <p className="text-slate-500 mb-6" data-id="element-323">
                    Your {vaccineName} appointment has been scheduled. A
                    confirmation email has been sent to you.
                  </p>
                  <div className="bg-slate-50 p-4 rounded-2xl inline-flex items-center gap-3 text-slate-600 text-sm font-medium" data-id="element-324">
                    <MapPinIcon className="w-4 h-4 text-clinical-blue" data-id="element-325" />
                    Walk-ins are also welcome anytime!
                  </div>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-326">
                  <div className="space-y-4" data-id="element-327">
                    <div className="space-y-2" data-id="element-328">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-329">
                        Full Name
                      </label>
                      <input required type="text" placeholder="John Doe" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-330" />
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-331">
                      <div className="space-y-2" data-id="element-332">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-333">
                          Email Address
                        </label>
                        <input required type="email" placeholder="john@example.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-334" />
                      </div>
                      <div className="space-y-2" data-id="element-335">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-336">
                          Phone Number
                        </label>
                        <input required type="tel" placeholder="(555) 000-0000" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-337" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-338">
                      <div className="space-y-2" data-id="element-339">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-340">
                          Preferred Date
                        </label>
                        <div className="relative" data-id="element-341">
                          <input required type="date" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-342" />
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-343">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-344">
                          Preferred Time
                        </label>
                        <select required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all appearance-none" data-id="element-345">
                          <option value="" data-id="element-346">Select Time</option>
                          <option data-id="element-347">9:00 AM - 11:00 AM</option>
                          <option data-id="element-348">11:00 AM - 1:00 PM</option>
                          <option data-id="element-349">1:00 PM - 3:00 PM</option>
                          <option data-id="element-350">3:00 PM - 5:00 PM</option>
                          <option data-id="element-351">5:00 PM - 7:00 PM</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-352">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-353">
                        Select Store Location
                      </label>
                      <select required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all appearance-none" data-id="element-354">
                        <option value="" data-id="element-355">Select Location</option>
                        <option data-id="element-356">Annapolis Pharmacy (2002 Jennifer Rd)</option>
                        <option data-id="element-357">
                          Washington D.C. Pharmacy (1234 Pennsylvania Ave NW)
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-358">
                    <button type="submit" className="w-full bg-clinical-blue text-white font-black py-4 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs shadow-lg shadow-clinical-blue/20" data-id="element-359">
                      Schedule Appointment
                    </button>
                    <p className="mt-4 text-center text-[10px] text-slate-400 uppercase tracking-widest font-bold" data-id="element-360">
                      No payment required at this time
                    </p>
                  </div>
                </form>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}